import { ENDPOINTS, getFullUrl } from "./config";
import { Order } from "../../types/orders";
import {
  DaywiseOrderReportResponse,
  LabTestOrderData,
  LabTestOrderReportResponse,
  OfflineOrderData,
  OfflineOrderReportResponse,
  OnlineOrderReportData,
  OnlineOrderReportResponse,
} from "../../components/Report/Orders/Types";

export class OrderService {
  static getToken(): string | null {
    return localStorage.getItem("token");
  }
  static getRole(): string | null {
    return localStorage.getItem("auth_user");
  }

  static async getOrdersListing({
    page = 1,
    pageSize = 10,
    filter,
    medicalCampId,
  }: {
    page?: number;
    pageSize?: number;
    filter: any;
    medicalCampId?: string;
  }): Promise<{
    success: boolean;
    message?: string;
    data?: {
      totalItemsCount: number;
      totalPages: number;
      currentPage: number;
      orders: Order[];
    };
  }> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        filters: JSON.stringify(filter),
      });

      const url = `${getFullUrl(
        ENDPOINTS.ORDERS.GETORDERLISTING
      )}?${params.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const json = await response.json();

      return json.data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch medicine listing",
      };
    }
  }

  static async getOrderDetailsById(orderId: number): Promise<{
    success: boolean;
    message?: string;
    data?: {
      orderDetails: any;
      labTestDetails: any;
      productDetails: any[];
    };
  }> {
    try {
      const url = getFullUrl(
        ENDPOINTS.ORDERS.GETORDERDETAILS.replace(":id", orderId.toString())
      );

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const json = await response.json();

      return json.data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to fetch order details",
      };
    }
  }

 static async getClinicList() {
  try {
    const response = await fetch(
      getFullUrl(ENDPOINTS.ORDERS.GETCLINICSNAME),
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      }
    );

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(
        `Failed to fetch clinic list: ${response.status} ${errorBody}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Error in getClinicList:", error);
  }
}


  // In your OrderService.ts
  static async updateOrderStatus(
    id: number,
    orderStatus: string,
    clinicId: string
  ): Promise<{
    success: boolean;
    message: string;
    data: Order; // Using your existing Order interface
  }> {
    try {
      const response = await fetch(
        getFullUrl(ENDPOINTS.ORDERS.UPDATEORDERSTATUS),
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.getToken()}`,
          },
          body: JSON.stringify({ id, orderStatus, clinicId }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const json = await response.json();
      return json;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error
            ? error.message
            : "Failed to update order status",
        data: null as any,
      };
    }
  }

  static async getAdminPatientDetailsById(patientId: string) {
    try {
      const response = await fetch(
        getFullUrl(
          `${ENDPOINTS.ORDERS.GETPATIENTDETAILS}?patientId=${patientId}`
        ),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.getToken()}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const data = await response.json();
      console.log("details of medicine", data);
      return data;
    } catch (error) {
      console.error("Failed to fetch order details:", error);
      throw error;
    }
  }

  static async getDashboardOverview(clinicId: string) {
    try {
      const params = new URLSearchParams({ clinicId });
      const url = `${getFullUrl(
        ENDPOINTS.INVENTORY.DASHBOARDOVERVIEW
      )}?${params}`;

      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
        method: "GET",
      });

      if (response.status === 401) {
        return {
          success: "invalid",
          message: "Session expired. Please log in again.",
        };
      }

      const data = await response.json();
      console.log("dashboard response", data);
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to fetch medicines",
      };
    }
  }

  static async generateBill(orderId: number, orderType: string) {
    try {
      const params = new URLSearchParams({
        orderId: orderId.toString(),
        orderType,
      });
      let url = `${getFullUrl(
        ENDPOINTS.ORDERS.GENERATEBILL
      )}/online-order/bill?${params}`;
      if (orderType === "OFFLINE") {
        url = `${getFullUrl(
          ENDPOINTS.ORDERS.GENERATEBILL
        )}/offline/bill?${params}`;
      }
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      if (response.status === 401) {
        return {
          success: "invalid",
          message: "Session expired. Please log in again.",
        };
      }

      const data = await response.json();
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Failed to Generate Bill",
      };
    }
  }

  static async checkOrder(orderId: number): Promise<boolean> {
    try {
      const params = new URLSearchParams({ orderId: orderId.toString() });
      const url = `${getFullUrl(ENDPOINTS.ORDERS.CHECKORDER)}?${params}`;

      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
        method: "GET",
      });

      const data = await response.json();
      console.log("IS name required", data);
      return data.doctorNameReq;
    } catch (error) {
      console.error("Error checking order:", error);
      return false;
    }
  }

  static async checkProduct(productId: string): Promise<boolean> {
    try {
      const params = new URLSearchParams({ productId });
      const url = `${getFullUrl(ENDPOINTS.ORDERS.CHECKPRODUCT)}?${params}`;

      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
        method: "GET",
      });

      const data = await response.json();
      console.log("IS name required", data);
      return data.doctorNameReq;
    } catch (error) {
      console.error("Error checking order:", error);
      return false;
    }
  }

  static async getOnlineOrderReport(
    filters: any,
    page: number,
    pageSize: number,
    isComplete: boolean
  ): Promise<OnlineOrderReportData> {
    try {
      const params = new URLSearchParams();
      console.log("filters", filters);
      if (filters.startDate) {
        params.set("startDate", filters.startDate);
      }
      if (filters.endDate) {
        params.set("endDate", filters.endDate);
      }
      if (filters.orderType) {
        params.set("orderType", filters.orderType);
      }
      if (filters.orderStatus) {
        params.set("orderStatus", filters.orderStatus);
      }
      if (filters.clinicId) {
        params.set("clinicId", filters.clinicId);
      }
      if (filters.orderDate) {
        params.set("orderDate", filters.orderDate);
      }
      if (filters.searchBy) {
        params.set("searchBy", filters.searchBy);
      }
      if (filters.searchText) {
        params.set("searchText", filters.searchText);
      }
      if (page) {
        params.set("page", page.toString());
      }
      if (pageSize) {
        params.set("limit", pageSize.toString());
      }
      if (isComplete) {
        params.set("isCompleteReport", isComplete.toString());
      }

      const url = `${getFullUrl(
        ENDPOINTS.REPORT.GET_ONLINE_ORDER_REPORT
      )}?${params}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const json: OnlineOrderReportResponse = await response.json();
      return json.data;
    } catch (error) {
      throw error;
    }
  }

  static async getOfflineOrderReport(
    filters: any,
    page: number,
    pageSize: number,
    isComplete: boolean
  ): Promise<OfflineOrderData> {
    try {
      const params = new URLSearchParams();
      if (filters.startDate) {
        params.set("startDate", filters.startDate);
      }
      if (filters.endDate) {
        params.set("endDate", filters.endDate);
      }
      if (filters.orderType) {
        params.set("orderType", filters.orderType);
      }
      if (filters.orderStatus) {
        params.set("orderStatus", filters.orderStatus);
      }
      if (filters.clinicId) {
        params.set("clinicId", filters.clinicId);
      }
      if (filters.orderDate) {
        params.set("orderDate", filters.orderDate);
      }
      if (filters.searchBy) {
        params.set("searchBy", filters.searchBy);
      }
      if (filters.searchText) {
        params.set("searchText", filters.searchText);
      }
      if (page) {
        params.set("page", page.toString());
      }
      if (pageSize) {
        params.set("limit", pageSize.toString());
      }
      if (isComplete) {
        params.set("isCompleteReport", isComplete.toString());
      }

      console.log(params)

      const url = `${getFullUrl(
        ENDPOINTS.REPORT.GET_OFFLINE_ORDER_REPORT
      )}?${params}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const json: OfflineOrderReportResponse = await response.json();
      return json.data;
    } catch (error) {
      throw error;
    }
  }

  static async getLabtestOrderReport(
    filters: any,
    page: number,
    pageSize: number,
    isComplete: boolean
  ): Promise<LabTestOrderData> {
    try {
      const params = new URLSearchParams();
      if (filters.startDate) {
        params.set("startDate", filters.startDate);
      }
      if (filters.endDate) {
        params.set("endDate", filters.endDate);
      }
      if (filters.orderType) {
        params.set("orderType", filters.orderType);
      }
      if (filters.orderStatus) {
        params.set("orderStatus", filters.orderStatus);
      }
      if (filters.clinicId) {
        params.set("clinicId", filters.clinicId);
      }
      if (filters.orderDate) {
        params.set("orderDate", filters.orderDate);
      }
      if (filters.searchBy) {
        params.set("searchBy", filters.searchBy);
      }
      if (filters.searchText) {
        params.set("searchText", filters.searchText);
      }
      if (page) {
        params.set("page", page.toString());
      }
      if (pageSize) {
        params.set("limit", pageSize.toString());
      }
      if (isComplete) {
        params.set("isCompleteReport", isComplete.toString());
      }

      const url = `${getFullUrl(
        ENDPOINTS.REPORT.GET_LABTEST_ORDER_REPORT
      )}?${params}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const json: LabTestOrderReportResponse = await response.json();
      return json.data;
    } catch (error) {
      throw error;
    }
  }

  static async getOnlineDaywiseReport(filters:any,page:number,pageSize:number):Promise<DaywiseOrderReportResponse>{
    try {
        const params=new URLSearchParams();
        if(filters.startDate){
            params.set("startDate",filters.startDate);
        }
        if(filters.endDate){
            params.set("endDate",filters.endDate);
        }
        if(filters.clinicId){
            params.set("clinicId",filters.clinicId);
        }
        if(page){
            params.set("page",page.toString());
        }
        if(pageSize){
            params.set("limit",pageSize.toString());
        }
        const url=`${getFullUrl(ENDPOINTS.REPORT.GET_ONLINE_DAYWISE_REPORT)}?${params}`;
        const response=await fetch(url,{
            method:"GET",
            headers:{
                "Content-Type":"application/json",
                Authorization:`Bearer ${this.getToken()}`,
            },
        });
        if(!response.ok){
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const json:DaywiseOrderReportResponse=await response.json();
        return json;
    } catch (error) {
        throw error;
    }
  }

  static async getOfflineDaywiseReport(filters:any,page:number,pageSize:number):Promise<DaywiseOrderReportResponse>{
    try {
        const params=new URLSearchParams();
        if(filters.startDate){
            params.set("startDate",filters.startDate);
        }
        if(filters.endDate){
            params.set("endDate",filters.endDate);
        }
        if(filters.clinicId){
            params.set("clinicId",filters.clinicId);
        }
        if(page){
            params.set("page",page.toString());
        }
        if(pageSize){
            params.set("limit",pageSize.toString());
        }
        const url=`${getFullUrl(ENDPOINTS.REPORT.GET_OFFLINE_DAYWISE_REPORT)}?${params}`;
        const response=await fetch(url,{
            method:"GET",
            headers:{
                "Content-Type":"application/json",
                Authorization:`Bearer ${this.getToken()}`,
            },
        });
        if(!response.ok){
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const json:DaywiseOrderReportResponse=await response.json();
        return json;
    } catch (error) {
        throw error;
    }
  }

  static async getOnlineRevenueSummary(filters:any):Promise<any>{
    try {
        const params=new URLSearchParams();
        if(filters.startDate){
            params.set("startDate",filters.startDate);
        }
        if(filters.endDate){
            params.set("endDate",filters.endDate);
        }
        if(filters.clinicId){
            params.set("clinicId",filters.clinicId);
        }
        const url=`${getFullUrl(ENDPOINTS.REPORT.GET_ONLINE_REVENUE_SUMMARY)}?${params}`;
        const response=await fetch(url,{
            method:"GET",
            headers:{
                "Content-Type":"application/json",
                Authorization:`Bearer ${this.getToken()}`,
            },
        });
        if(!response.ok){
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const json=await response.json();
        return json;
    } catch (error) {
        throw error;
    }
  }

  static async getOfflineRevenueSummary(filters:any):Promise<any>{
    try {
        const params=new URLSearchParams();
        if(filters.startDate){
            params.set("startDate",filters.startDate);
        }
        if(filters.endDate){
            params.set("endDate",filters.endDate);
        }
        if(filters.clinicId){
            params.set("clinicId",filters.clinicId);
        }
        const url=`${getFullUrl(ENDPOINTS.REPORT.GET_OFFLINE_REVENUE_SUMMARY)}?${params}`;
        const response=await fetch(url,{
            method:"GET",
            headers:{
                "Content-Type":"application/json",
                Authorization:`Bearer ${this.getToken()}`,
            },
        });
        if(!response.ok){
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const json=await response.json();
        return json;
    } catch (error) {
        throw error;
    }
  }
}
